import { useEffect } from 'react';

import { get } from '../../api';
import T from '../../translate';
import { useGlobalStore } from '../state/globalStore';
import { API_ERROR_NAMES, ENTITY_TYPE } from '../utils/constants';
import {
  formatFields,
  getCookie,
  getEntityCookiePassword,
  getLeadCaptureUrl,
  responseTransformer,
} from '../utils/helper';
import { reportErrorToPlayer } from '../utils/player-controller';
import { ERROR_CODES } from '../utils/player-controller/constants';
import { Enum, LeadCapture } from '../utils/types';
import { useLeadFormPrefillChildListener } from './useLeadFormPrefill';
import { useNotification } from './useNotification';

/**
 * This hook fetches the lead capture data and initialize the lead capture zustand store with the data.
 */
export const useLeadCapture = () => {
  const entityId = useGlobalStore((store) => store.apiEntityId);
  const entityType = useGlobalStore((store) => store.entityType);
  const { show: showNotification } = useNotification();

  const { parentLeadUuid, parentEntityType } = useLeadFormPrefillChildListener(
    entityId,
    entityType as Enum<typeof ENTITY_TYPE>,
  );

  const getLeadCaptureData = async (
    entityType: Enum<typeof ENTITY_TYPE>,
    entityId: string,
    signal: AbortSignal,
  ) => {
    try {
      const leadId = getCookie(entityId);
      const entityPassword = getEntityCookiePassword(entityId, entityType);
      const lead_capture_url = getLeadCaptureUrl(entityPassword, leadId, undefined, parentLeadUuid);

      let data = await get(lead_capture_url, { signal });
      let finalResponse = {} as LeadCapture;
      // Run the values through transformer
      for (const [key, value] of Object.entries(data as any)) {
        (finalResponse as any)[key] = responseTransformer(key, value, {
          resolveTags: true,
        });
      }

      finalResponse.customFields = formatFields(finalResponse.customFields);
      finalResponse.hiddenFields = formatFields(finalResponse.hiddenFields);

      useGlobalStore.setState({
        leadCapture: finalResponse as LeadCapture,
        parentLeadUuid: parentLeadUuid,
        parentEntityType: parentEntityType,
      });
    } catch (error) {
      /** No need to report if api call is aborted using abort signal */
      if (error.name !== API_ERROR_NAMES.ABORT) {
        reportErrorToPlayer({
          code: ERROR_CODES.LEAD_CAPTURE_FORM_FETCHING_FAILED,
          message: 'Error fetching lead capture form data',
        });
        showNotification({ message: T.UhOhThereWasAProblem });
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    if (entityId && entityType) {
      getLeadCaptureData(entityType, entityId, controller.signal);
    }
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType, parentLeadUuid]);
};
